import { useConvexAction, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@rekrytering/ui/components/dropdown-menu'
import { useMutation } from '@tanstack/react-query'
import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PdfExportDrawer } from '@/features/interviews/components/pdf-export-drawer'

type DrawerState =
  | { readonly open: false }
  | { readonly open: true; readonly exportType: 'transcripts' | 'reports' }

type AggregatedDownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'generating' }
  | { readonly status: 'error' }

type PdfExportDropdownProps = {
  readonly jobId: Id<'jobs'>
}

export const PdfExportDropdown = ({ jobId }: PdfExportDropdownProps) => {
  const { t } = useTranslation()
  const [drawerState, setDrawerState] = useState<DrawerState>({ open: false })
  const [aggregatedState, setAggregatedState] =
    useState<AggregatedDownloadState>({ status: 'idle' })

  const { mutateAsync: createExportJob } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.pdf_export_public.createMyPdfExportJob,
    ),
  })

  const { mutateAsync: generateAggregatePdf } = useMutation({
    mutationFn: useConvexAction(
      api.features.interviews.pdf_export.generateMyAggregateReportPdf,
    ),
  })

  const handleDownloadAggregated = async (): Promise<void> => {
    setAggregatedState({ status: 'generating' })

    const result = await createExportJob({
      jobId,
      type: 'aggregated',
      candidateCount: 1,
    }).catch(() => null)

    if (!result) {
      setAggregatedState({ status: 'error' })
      return
    }

    const downloadResult = await generateAggregatePdf({
      exportJobId: result.exportJobId,
      jobId,
    }).catch(() => null)

    if (!downloadResult || !downloadResult.ok) {
      setAggregatedState({ status: 'error' })
      return
    }

    const anchor = document.createElement('a')
    anchor.href = downloadResult.downloadUrl
    anchor.download = ''
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    setAggregatedState({ status: 'idle' })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="sm"
              disabled={aggregatedState.status === 'generating'}
            />
          }
        >
          {t('interviews.pdfExport.downloadButton')}
          <ChevronDownIcon className="ml-1 size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={handleDownloadAggregated}
            disabled={aggregatedState.status === 'generating'}
          >
            {aggregatedState.status === 'generating'
              ? t('interviews.pdfExport.generating', {
                  completed: '0',
                  total: '1',
                })
              : t('interviews.pdfExport.aggregatedReport')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              setDrawerState({ open: true, exportType: 'transcripts' })
            }
          >
            {t('interviews.pdfExport.transcripts')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              setDrawerState({ open: true, exportType: 'reports' })
            }
          >
            {t('interviews.pdfExport.individualReports')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PdfExportDrawer
        open={drawerState.open}
        onOpenChange={(open) => {
          if (!open) setDrawerState({ open: false })
        }}
        jobId={jobId}
        exportType={drawerState.open ? drawerState.exportType : 'transcripts'}
      />
    </>
  )
}
